
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  UserCheck,
  Search,
  MoreVertical,
  Shield,
  Mail,
  Lock,
  Filter,
  Check,
  X,
  Edit2,
  RefreshCw,
  Copy,
  Trash2
} from 'lucide-react';
import { MOCK_USERS } from '../constants';
import { apiUrl } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

interface User {
  id: string;
  username: string;
  email: string;
  company_name: string | null;
  org_company_name?: string | null; // Adding this too just in case
  role: string | null;
  is_active: boolean;
  created_at: string;
}

const UsersAccess: React.FC = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'roles' | 'policies'>('users');
  const [registeredUsers, setRegisteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof User>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [orgInviteCode, setOrgInviteCode] = useState<string | null>(null);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [currentOrg, setCurrentOrg] = useState<any>(null);
  const [securitySettings, setSecuritySettings] = useState({
    enforce_sso: false,
    session_timeout: 12,
  });
  const [ipRanges, setIpRanges] = useState<string[]>([]);
  const [isSavingPolicies, setIsSavingPolicies] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [newTimeoutValue, setNewTimeoutValue] = useState(12);
  const [showIPModal, setShowIPModal] = useState(false);
  const [newIPRange, setNewIPRange] = useState('');

  useEffect(() => {
    if (token) {
      fetchRegisteredUsers();
    }
  }, [token]);

  useEffect(() => {
    if (token && user) {
      // Only fetch/show invite code for Org Admins for their specific org
      if (user.role === 'Org Admin') {
        setIsOrgAdmin(true);
        fetchOrgInviteCode();
      } else {
        setIsOrgAdmin(false);
        setOrgInviteCode(null);
      }
    }
  }, [token, user]);

  const fetchOrgInviteCode = async () => {
    if (!token || !(user as any)?.company_name) return;
    try {
      const response = await fetch(apiUrl('/api/auth/organizations'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Find the organization that matches the user's company
        const myOrg = data.find((org: any) => org.name === (user as any).company_name);
        if (myOrg) {
          setOrgInviteCode(myOrg.invite_code);
        }
      }
    } catch (err) {
      console.error('Fetch org error:', err);
    }
  };

  const fetchCurrentOrgDetails = async () => {
    if (!token || !user) return;
    try {
      const response = await fetch(apiUrl('/api/auth/organizations'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();

        let myOrg = null;
        if (data.length === 1) {
          // For Org Admins, the API only returns their own organization
          myOrg = data[0];
        } else if (data.length > 1) {
          const companyName = (user as any).company_name?.toLowerCase();
          // Try exact match
          myOrg = data.find((org: any) => org.name.toLowerCase() === companyName);

          // Fallback fuzzy match for Super Admins (handles typos like engazeiq vs engaziq)
          if (!myOrg && user.role === 'Super Admin' && companyName) {
            myOrg = data.find((org: any) =>
              org.name.toLowerCase().includes(companyName) ||
              companyName.includes(org.name.toLowerCase())
            );
          }

          // Absolute fallback for Super Admin
          if (!myOrg && user.role === 'Super Admin') {
            myOrg = data[0];
          }
        }

        if (myOrg) {
          setCurrentOrg(myOrg);
          setSecuritySettings({
            enforce_sso: myOrg.enforce_sso,
            session_timeout: myOrg.session_timeout,
          });
          setIpRanges(myOrg.ip_filtering ? myOrg.ip_filtering.split(',').filter(Boolean) : []);
        }
      }
    } catch (err) {
      console.error('Fetch org details error:', err);
    }
  };

  useEffect(() => {
    if (token && user && activeTab === 'policies') {
      fetchCurrentOrgDetails();
    }
  }, [token, user, activeTab]);

  const updateOrgPolicies = async (updates: any) => {
    if (!token || !currentOrg) return;
    setIsSavingPolicies(true);
    try {
      const response = await fetch(apiUrl(`/api/auth/organizations/${currentOrg.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        const updatedOrg = await response.json();
        setCurrentOrg(updatedOrg);
        setSecuritySettings({
          enforce_sso: updatedOrg.enforce_sso,
          session_timeout: updatedOrg.session_timeout,
        });
        setIpRanges(updatedOrg.ip_filtering ? updatedOrg.ip_filtering.split(',').filter(Boolean) : []);
      } else {
        alert('Failed to update policies');
      }
    } catch (err) {
      console.error('Update policies error:', err);
      alert('An error occurred while updating policies');
    } finally {
      setIsSavingPolicies(false);
    }
  };

  const handleToggleSSO = () => {
    updateOrgPolicies({ enforce_sso: !securitySettings.enforce_sso });
  };

  const handleSaveTimeout = () => {
    updateOrgPolicies({ session_timeout: newTimeoutValue });
    setShowTimeoutModal(false);
  };

  const handleAddIP = () => {
    if (!newIPRange) return;
    const updatedIPs = [...ipRanges, newIPRange];
    updateOrgPolicies({ ip_filtering: updatedIPs.join(',') });
    setNewIPRange('');
    setShowIPModal(false);
  };

  const handleDeleteIP = (ipToDelete: string) => {
    const updatedIPs = ipRanges.filter(ip => ip !== ipToDelete);
    updateOrgPolicies({ ip_filtering: updatedIPs.join(',') });
  };

  const handleRegenerateCode = async () => {
    if (!token || !(user as any)?.company_name) return; // Need company name to find ID
    try {
      // We need the Org ID to regenerate. 
      // Optimized: We could store Org ID in state, but simpler to fetch logical flow for now or store full org object.
      // Re-fetching to find ID is safe enough for this admin action.
      const orgResponse = await fetch(apiUrl('/api/auth/organizations'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (orgResponse.ok) {
        const data = await orgResponse.json();
        const myOrg = data.find((org: any) => org.name === (user as any).company_name);

        if (myOrg) {
          const response = await fetch(apiUrl(`/api/auth/organizations/${myOrg.id}/regenerate-invite-code`), {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const result = await response.json();
            setOrgInviteCode(result.invite_code);
          }
        }
      }
    } catch (err) {
      console.error('Regenerate error:', err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Invite code copied to clipboard!');
  };

  const fetchRegisteredUsers = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(apiUrl('/api/auth/users'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('API Error:', response.status, errorData);
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const data = await response.json();
      console.log('Fetched users:', data);
      setRegisteredUsers(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while fetching users';
      console.error('Fetch error:', errorMessage, err);
      setError(errorMessage);
      setRegisteredUsers([]);
    } finally {
      setLoading(false);
    }
  };



  const handleEditUser = (user: User) => {
    setEditingUser({ ...user });
    setShowEditModal(true);
    setOpenMenuId(null);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    try {
      const response = await fetch(apiUrl(`/api/auth/users/${editingUser.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: editingUser.username,
          email: editingUser.email,
          company_name: editingUser.company_name,
          role: editingUser.role,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update user');
      }

      const updatedUser = await response.json();
      setRegisteredUsers(registeredUsers.map(u => u.id === updatedUser.id ? updatedUser : u));
      setShowEditModal(false);
      setEditingUser(null);
    } catch (err) {
      console.error('Update error:', err);
      alert(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userToDelete: User) => {
    if (!window.confirm(`Are you sure you want to delete ${userToDelete.username}?`)) {
      return;
    }

    try {
      const response = await fetch(apiUrl(`/api/auth/users/${userToDelete.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        let errorMessage = 'Failed to delete user';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) { }
        throw new Error(errorMessage);
      }

      setRegisteredUsers(registeredUsers.filter(u => u.id !== userToDelete.id));
      setOpenMenuId(null);
    } catch (err) {
      console.error('Delete error:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail) return;

    setInviteLoading(true);
    try {
      const response = await fetch(apiUrl('/api/auth/invite'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: inviteEmail }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send invitation');
      }

      alert(`Invitation sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
    } catch (err) {
      console.error('Invite error:', err);
      alert(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const filteredUsers = registeredUsers.filter((userItem) => {
    if (user?.role?.toLowerCase() === 'builder' && userItem.email !== user?.email) {
      return false;
    }
    const searchLower = searchQuery.toLowerCase();
    return (
      (userItem.username || '').toLowerCase().includes(searchLower) ||
      (userItem.email || '').toLowerCase().includes(searchLower) ||
      (userItem.company_name || '').toLowerCase().includes(searchLower) ||
      (userItem.role || '').toLowerCase().includes(searchLower)
    );
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aValue: any = a[sortColumn];
    let bValue: any = b[sortColumn];

    // Handle null/undefined values
    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    // Convert to lowercase for string comparison
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (column: keyof User) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleExportCSV = () => {
    if (sortedUsers.length === 0) {
      alert('No users to export');
      return;
    }

    // Prepare CSV content
    const headers = ['Username', 'Email', 'Company', 'Role', 'Status', 'Registered Date'];
    const rows = sortedUsers.map((user) => [
      user.username,
      user.email,
      user.company_name || '-',
      user.role || '-',
      user.is_active ? 'Active' : 'Inactive',
      new Date(user.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }),
    ]);

    // Create CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((cell) =>
            typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))
              ? `"${cell.replace(/"/g, '""')}"`
              : cell
          )
          .join(',')
      ),
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Users & Access</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Manage platform members, permissions, and security policies.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-300 shadow-sm transition-all"
          >
            <UserCheck size={18} /> Register User
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all"
          >
            <UserPlus size={18} /> Invite User
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 dark:border-slate-800">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'users' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Users
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'roles' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Roles & Permissions
        </button>
        <button
          onClick={() => setActiveTab('policies')}
          className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'policies' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Access Policies
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="space-y-4">

          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm transition-colors duration-300">
            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative max-w-sm w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 text-slate-900 dark:text-slate-100"
                />
              </div>
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all text-slate-600">
                  <Filter size={18} />
                </button>
                <button
                  onClick={handleExportCSV}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all"
                >
                  Export CSV
                </button>
              </div>
            </div>

            {loading && (
              <div className="p-8 text-center text-slate-500">
                <p>Loading registered users...</p>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-50 border-b border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 uppercase text-[10px] font-bold tracking-wider">
                    <th
                      onClick={() => handleSort('username')}
                      className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                    >
                      User {sortColumn === 'username' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('email')}
                      className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                    >
                      Email {sortColumn === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('company_name')}
                      className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                    >
                      Company {sortColumn === 'company_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('role')}
                      className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                    >
                      Role {sortColumn === 'role' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('is_active')}
                      className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                    >
                      Status {sortColumn === 'is_active' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('created_at')}
                      className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                    >
                      Registered {sortColumn === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {sortedUsers && sortedUsers.length > 0 ? (
                    sortedUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-500 overflow-hidden">
                              <img src={`https://picsum.photos/seed/${user.id}/40`} alt={user.username} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-900 dark:text-white">{user.username}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700 dark:text-slate-300">{user.email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700 dark:text-slate-300">{user.company_name || '-'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {user.role || 'User'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5">
                            <div className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`}></div>
                            <span className="text-xs text-slate-700 dark:text-slate-300">{user.is_active ? 'Active' : 'Inactive'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-500">
                          {new Date(user.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-6 py-4 text-right relative">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)}
                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded text-slate-500"
                          >
                            <MoreVertical size={16} />
                          </button>
                          {openMenuId === user.id && (
                            <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-800 z-50">
                              <button
                                onClick={() => handleEditUser(user)}
                                className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800"
                              >
                                <Edit2 size={14} /> Edit
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user)}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    !loading && (
                      <tr>
                        <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                          No registered users found
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'roles' && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-8 shadow-sm transition-colors duration-300">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-6">Permission Matrix</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] uppercase font-bold text-slate-400">
                    <th className="pb-4 pr-6">Feature</th>
                    <th className="pb-4 px-4 text-center">Super Admin</th>
                    <th className="pb-4 px-4 text-center">Org Admin</th>
                    <th className="pb-4 px-4 text-center">Builder</th>
                    <th className="pb-4 px-4 text-center">Viewer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    "Agent Builder Access",
                    "Execute Workflows",
                    "Manage API Models",
                    "View Billing",
                    "Manage Users",
                    "Export Audit Logs"
                  ].map((feat, i) => (
                    <tr key={i}>
                      <td className="py-4 pr-6 text-sm font-medium text-slate-700 dark:text-slate-300">{feat}</td>
                      <td className="py-4 px-4 text-center">
                        <Check className="mx-auto text-blue-600" size={18} />
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Check className="mx-auto text-blue-600" size={18} />
                      </td>
                      <td className="py-4 px-4 text-center">
                        {i !== 4 ? <Check className="mx-auto text-blue-600" size={18} /> : <X className="mx-auto text-slate-300" size={18} />}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {i === 1 ? <Check className="mx-auto text-blue-600" size={18} /> : <X className="mx-auto text-slate-300" size={18} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'policies' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm transition-colors duration-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                <Lock size={20} />
              </div>
              <h3 className="font-bold text-slate-900 dark:text-slate-100">Security Settings</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Enforce SSO</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Only Google/Azure logins allowed.</p>
                </div>
                <div
                  onClick={handleToggleSSO}
                  className={`w-12 h-6 rounded-full relative p-1 cursor-pointer transition-colors ${securitySettings.enforce_sso ? 'bg-blue-600' : 'bg-slate-300'}`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full absolute transition-all ${securitySettings.enforce_sso ? 'right-1' : 'left-1'}`}></div>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Session Timeout</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Duration: {securitySettings.session_timeout} Hours</p>
                </div>
                <button
                  onClick={() => {
                    setNewTimeoutValue(securitySettings.session_timeout);
                    setShowTimeoutModal(true);
                  }}
                  className="text-blue-600 text-xs font-bold"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg">
                <Shield size={20} />
              </div>
              <h3 className="font-bold text-slate-900 dark:text-slate-100">IP Filtering</h3>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Restricting dashboard access to corporate VPN IP ranges.</p>
            <div className="space-y-2">
              {ipRanges.map((ip, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-lg">
                  <span className="font-mono text-slate-600 dark:text-slate-400">{ip}</span>
                  <button
                    onClick={() => handleDeleteIP(ip)}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setShowIPModal(true)}
                className="w-full mt-2 py-2 border-2 border-dashed border-slate-200 rounded-xl text-xs font-bold text-slate-400 hover:border-blue-400 hover:text-blue-600 transition-all"
              >
                + Add IP Range
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-md w-full mx-4 border border-slate-200 dark:border-slate-700">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Edit User</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Username</label>
                <input
                  type="text"
                  value={editingUser.username}
                  onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                <input
                  type="text"
                  value={editingUser.company_name || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, company_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Role</label>
                <select
                  value={editingUser.role || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  <option value="">Select Role</option>
                  <option value="Super Admin">Super Admin</option>
                  <option value="Org Admin">Org Admin</option>
                  <option value="Workspace Admin">Workspace Admin</option>
                  <option value="Builder">Builder</option>
                  <option value="Viewer">Viewer</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                }}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-md w-full mx-4 border border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Invite User</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm">
              Invite a new user to your organization.
            </p>

            {/* Invite Code Section (Only for Org Admins) */}
            {orgInviteCode && (
              <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Organization Invite Code</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg font-mono text-sm font-bold text-blue-600 dark:text-blue-400">
                    {orgInviteCode}
                  </code>
                  <button
                    onClick={() => copyToClipboard(orgInviteCode)}
                    className="p-2 hover:bg-white dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-all text-slate-400 dark:text-slate-500"
                    title="Copy code"
                  >
                    <Copy size={18} />
                  </button>
                  {isOrgAdmin && (
                    <button
                      onClick={handleRegenerateCode}
                      className="p-2 hover:bg-white dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-all text-slate-400 dark:text-slate-500"
                      title="Regenerate code"
                    >
                      <RefreshCw size={18} />
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Share this code with colleagues to let them join directly.
                </p>
              </div>
            )}

            {orgInviteCode && (
              <div className="relative flex items-center gap-4 mb-6">
                <div className="h-px bg-slate-200 flex-1"></div>
                <span className="text-xs font-bold text-slate-400 uppercase">OR</span>
                <div className="h-px bg-slate-200 flex-1"></div>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Email Invite</label>
              <input
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                }}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-all"
                disabled={inviteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleInviteUser}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                disabled={inviteLoading || !inviteEmail}
              >
                {inviteLoading ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Session Timeout Modal */}
      {showTimeoutModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-sm w-full mx-4 border border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Edit Session Timeout</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6 font-sm">Set the duration for user sessions in hours.</p>
            <input
              type="number"
              value={newTimeoutValue}
              onChange={(e) => setNewTimeoutValue(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-8"
              min="1"
              max="168"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowTimeoutModal(false)}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTimeout}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add IP Modal */}
      {showIPModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-sm w-full mx-4 border border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Add IP Range</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6 font-sm">Enter the IP range (e.g., 192.168.1.1/24).</p>
            <input
              type="text"
              value={newIPRange}
              placeholder="0.0.0.0/0"
              onChange={(e) => setNewIPRange(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-8"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowIPModal(false)}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddIP}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersAccess;
